import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, AlertCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { readJson } from '../lib/fetchHelpers';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TennisLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/tennis/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await readJson(response);

      if (!response.ok) {
        throw new Error(data.detail || t('tennis.loginFailed'));
      }

      // Store token and member info
      localStorage.setItem('tennis_token', data.token);
      localStorage.setItem('tennis_member', JSON.stringify(data.member));

      toast.success(t('tennis.welcomeBack'));
      
      // Redirect based on admin status
      if (data.member.is_admin) {
        navigate('/admin');
      } else {
        navigate('/tennis/booking');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tennis-login-page" className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Back button */}
        <Link to="/tennis" className="inline-flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('tennis.backToTennis')}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎾</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('tennis.login')}</h1>
          <p className="text-slate-500 mt-2">{t('tennis.bookingSystem')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="email"
                  data-testid="tennis-login-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="din@epost.no"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('tennis.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  data-testid="tennis-login-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="tennis-login-submit"
            className="w-full mt-6 bg-green-500 text-white font-bold hover:bg-green-500/90"
            disabled={loading}
          >
            {loading ? t('tennis.loggingIn') : t('tennis.submitLogin')}
          </Button>

          <div className="text-center mt-4">
            <Link to="/tennis/forgot-password" className="text-green-500 hover:underline text-sm">
              {t('tennis.forgotPasswordOrUsername')}
            </Link>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            {t('tennis.notMember')}{' '}
            <Link to="/tennis/register" className="text-green-500 hover:underline">
              {t('tennis.registerHere')}
            </Link>
          </p>
        </form>

        {/* Info box */}
        <div className="mt-6 bg-slate-100 dark:bg-slate-800/50 rounded-lg p-4">
          <p className="text-slate-500 dark:text-slate-400 text-sm text-center">
            <strong className="text-slate-900 dark:text-white">{t('tennis.tip')}:</strong> {t('tennis.approvalNote')}
          </p>
        </div>

        {/* Admin login link */}
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <Link
            to="/admin/login"
            data-testid="admin-login-link"
            className="flex items-center justify-center gap-2 text-slate-400 dark:text-slate-500 hover:text-green-500 dark:hover:text-green-400 text-xs transition-colors"
          >
            <Shield className="w-3.5 h-3.5" />
            {t('pg.TennisLogin.a4596e')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TennisLogin;
